import { formTranslations } from "./formTranslations";
import { thankYouTranslations } from "./thankYouTranslations";
import { leadRecoveryTranslations } from "./leadRecoveryTranslations";
import { aboutTranslations } from "./aboutTranslations";
import { internalToolsTranslations } from "./internalToolsTranslations";
import { legalTranslations } from "./legalTranslations";
import { workflowAutomationTranslations } from "./workflowAutomationTranslations";
import { dashboardsReportingTranslations } from "./dashboardsReportingTranslations";
import { spreadsheetRescueTranslations } from "./spreadsheetRescueTranslations";
import { aiAgentsTranslations } from "./aiAgentsTranslations";

const base = {
  en: {
    meta: {
      title: "Kranbos | Custom AI Automation Trained on Your Business Data",
      description:
        "Kranbos builds custom AI for growing businesses in any industry. Automate quotes, reports, and admin work, and get instant answers trained on your own files, all in a private, secure environment.",
    },
    nav: {
      home: "Home",
      leadRecovery: "Lead Recovery",
      webDesign: "Website Design and Development",
      services: "Services",
      about: "About",
      contact: "Contact",
      cta: "Free 15-Minute Lead Leak Check",
    },
    industries: {
      title: "Industries we serve",
      items: [
        "HVAC",
        "Plumbing",
        "Cleaning",
        "Restoration",
        "Property managers",
        "Moving",
        "Med spas",
      ],
    },
    otherServices: {
      title: "Need more than lead recovery?",
      learnMore: "Learn more",
      svc1Title: "Internal Tools",
      svc1Text: "Custom web apps and tools built specifically for your operations.",
      svc2Title: "Workflow Automation",
      svc2Text: "Connecting your existing software to eliminate manual data entry.",
      svc3Title: "Dashboards & Reporting",
      svc3Text: "Get a clear view of your business health with live data visualization.",
      svc4Title: "Spreadsheet Rescue",
      svc4Text: "Turning broken, slow spreadsheets into fast, reliable systems.",
    },
    call: {
      prompt: "Prefer to talk? Call",
      footerLabel: "Call us",
    },
    footer: {
      tagline: "Custom AI automation trained on your business data, for growing businesses in any industry.",
      copyrightPrefix: "©",
      copyrightSuffix: "Kranbos. All rights reserved.",
      navigation: "Navigation",
      solutions: "Solutions",
      privacyPolicy: "Privacy Policy",
      terms: "Terms & Conditions",
      insuredBy: "Insured & Covered by",
      trustBadges: "Security and compliance",
    },
    underConstruction: {
      title: "Under construction",
      text: "This page is coming soon. Check back shortly!",
      backHome: "Back to Home",
    },
  },
  es: {
    meta: {
      title: "Kranbos | Automatización con IA a Medida Entrenada con los Datos de tu Negocio",
      description:
        "Kranbos crea IA a medida para negocios en crecimiento de cualquier industria. Automatiza cotizaciones, reportes y tareas administrativas, y obtén respuestas instantáneas entrenadas con tus propios archivos, en un entorno privado y seguro.",
    },
    nav: {
      home: "Inicio",
      leadRecovery: "Recuperación de Leads",
      webDesign: "Diseño y Desarrollo Web",
      services: "Servicios",
      about: "Nosotros",
      contact: "Contacto",
      cta: "Chequeo Gratis de Fugas de Leads en 15 Minutos",
    },
    industries: {
      title: "Industrias que atendemos",
      items: [
        "HVAC",
        "Plomería",
        "Limpieza",
        "Restauración",
        "Administradores de propiedades",
        "Mudanzas",
        "Med spas",
      ],
    },
    otherServices: {
      title: "¿Necesitas más que recuperación de leads?",
      learnMore: "Saber más",
      svc1Title: "Herramientas Internas",
      svc1Text: "Aplicaciones web y herramientas a medida para tus operaciones.",
      svc2Title: "Automatización de Flujos",
      svc2Text: "Conectamos tu software existente para eliminar la carga manual de datos.",
      svc3Title: "Paneles y Reportes",
      svc3Text: "Obtén una vista clara de la salud de tu negocio con visualización de datos en vivo.",
      svc4Title: "Rescate de Hojas de Cálculo",
      svc4Text: "Convertimos hojas de cálculo rotas y lentas en sistemas rápidos y confiables.",
    },
    call: {
      prompt: "¿Prefieres hablar? Llama al",
      footerLabel: "Llámanos",
    },
    footer: {
      tagline:
        "Automatización con IA a medida, entrenada con los datos de tu negocio, para empresas en crecimiento de cualquier industria.",
      copyrightPrefix: "©",
      copyrightSuffix: "Kranbos. Todos los derechos reservados.",
      navigation: "Navegación",
      solutions: "Soluciones",
      privacyPolicy: "Política de Privacidad",
      terms: "Términos y Condiciones",
      insuredBy: "Asegurado y cubierto por",
      trustBadges: "Seguridad y cumplimiento",
    },
    underConstruction: {
      title: "En construcción",
      text: "Esta página estará disponible pronto. ¡Vuelve en breve!",
      backHome: "Volver al Inicio",
    },
  },
};

export const translations = {
  en: {
    ...base.en,
    ...formTranslations.en,
    ...thankYouTranslations.en,
    ...leadRecoveryTranslations.en,
    ...aboutTranslations.en,
    ...internalToolsTranslations.en,
    ...legalTranslations.en,
    ...workflowAutomationTranslations.en,
    ...dashboardsReportingTranslations.en,
    ...spreadsheetRescueTranslations.en,
    ...aiAgentsTranslations.en,
  },
  es: {
    ...base.es,
    ...formTranslations.es,
    ...thankYouTranslations.es,
    ...leadRecoveryTranslations.es,
    ...aboutTranslations.es,
    ...internalToolsTranslations.es,
    ...legalTranslations.es,
    ...workflowAutomationTranslations.es,
    ...dashboardsReportingTranslations.es,
    ...spreadsheetRescueTranslations.es,
    ...aiAgentsTranslations.es,
  },
};

/** Supported UI languages. */
export type Language = keyof typeof translations;

/** Shape of a single locale's translation tree. */
export type Translation = (typeof translations)[Language];
